import { Currency, CurrencyAmount, MATIC, EWT, JSBI, Pair, Percent, Price, TokenAmount } from '@stichting-allianceblock-foundation/alliance-dex-sdk'
import { useRouterContract } from 'hooks/useContract'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PairState, usePair } from '../../data/Reserves'
import { useTotalSupply } from '../../data/TotalSupply'
import { PoolEmpty } from '../../data/Empty'

import { useActiveWeb3React } from '../../hooks'
import { wrappedCurrency, wrappedCurrencyAmount } from '../../utils/wrappedCurrency'
import { AppDispatch, AppState } from '../index'
import { tryParseAmount } from '../swap/hooks'
import { useCurrencyBalances } from '../wallet/hooks'
import { Field, typeInput } from './actions'

import { isValidDecimal, formatToDecimals } from 'utils/userInputFormat'

const ZERO = JSBI.BigInt(0)

export function useMintState(): AppState['mint'] {
  return useSelector<AppState, AppState['mint']>(state => state.mint)
}

export function useMintActionHandlers(
  noLiquidity: boolean | undefined
): {
  onFieldAInput: (typedValue: string, currency: Currency | null | undefined) => void
  onFieldBInput: (typedValue: string, currency: Currency | null | undefined) => void
} {
  const dispatch = useDispatch<AppDispatch>()

  const onFieldAInput = useCallback(
    (typedValue: string, currency: Currency | null | undefined) => {
      if(!isValidDecimal(typedValue, currency)){
        typedValue=formatToDecimals(typedValue, currency?.decimals)
      }
      dispatch(typeInput({ field: Field.CURRENCY_A, typedValue, noLiquidity: noLiquidity === true }))
    },
    [dispatch, noLiquidity]
  )
  const onFieldBInput = useCallback(
    (typedValue: string, currency: Currency | null | undefined) => {
      if(!isValidDecimal(typedValue, currency)){
        typedValue=formatToDecimals(typedValue, currency?.decimals)
      }
      dispatch(typeInput({ field: Field.CURRENCY_B, typedValue, noLiquidity: noLiquidity === true }))
    },
    [dispatch, noLiquidity]
  )

  return {
    onFieldAInput,
    onFieldBInput
  }
}

export function useDerivedMintInfo(
  currencyA: Currency | undefined,
  currencyB: Currency | undefined
): {
  dependentField: Field
  currencies: { [field in Field]?: Currency }
  pair?: Pair | null
  pairState: PairState
  currencyBalances: { [field in Field]?: CurrencyAmount }
  parsedAmounts: { [field in Field]?: CurrencyAmount }
  price?: Price
  noLiquidity?: boolean,
  isEmpty?: Boolean,
  liquidityMinted?: TokenAmount
  poolTokenPercentage?: Percent
  error?: string
} {
  const [dependentAmount, setDependentAmount] = useState<CurrencyAmount | undefined>(undefined)
  const [parsedAmounts, setParsedAmounts] = useState<{ [field in Field]?: CurrencyAmount | undefined }>({CURRENCY_A: undefined, CURRENCY_B: undefined})
  const { account, chainId } = useActiveWeb3React()
  const { independentField, typedValue, otherTypedValue } = useMintState()
  const dependentField = independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A

  // tokens
  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: currencyA ?? undefined,
      [Field.CURRENCY_B]: currencyB ?? undefined
    }),
    [currencyA, currencyB]
  )

  // pair
  const [pairState, pair] = usePair(currencies[Field.CURRENCY_A], currencies[Field.CURRENCY_B])
  const totalSupply = useTotalSupply(pair?.liquidityToken)
  const isEmpty = PoolEmpty(pair?.liquidityToken)
  const noLiquidity: boolean =
    pairState === PairState.NOT_EXISTS || Boolean(totalSupply && JSBI.equal(totalSupply.raw, ZERO) || isEmpty)


  // balances
  const balances = useCurrencyBalances(account ?? undefined, [
    currencies[Field.CURRENCY_A],
    currencies[Field.CURRENCY_B]
  ])
  const currencyBalances: { [field in Field]?: CurrencyAmount } = {
    [Field.CURRENCY_A]: balances[0],
    [Field.CURRENCY_B]: balances[1]
  }

  // amounts
  const independentAmount: CurrencyAmount | undefined = tryParseAmount(typedValue, currencies[independentField])
  
  const routerContract = useRouterContract();
  
  async function getDependentCurrency() {
    if (noLiquidity) {
      if (otherTypedValue && currencies[dependentField]) {
        setDependentAmount(tryParseAmount(otherTypedValue, currencies[dependentField]))
        return;
      }
      setDependentAmount(undefined)

      return;
    } else if (independentAmount) {
      // we wrap the currencies just to get the price in terms of the other token
      const wrappedIndependentAmount = wrappedCurrencyAmount(independentAmount, chainId)
      const [tokenA, tokenB] = [wrappedCurrency(currencyA, chainId), wrappedCurrency(currencyB, chainId)]
      if (tokenA && tokenB && wrappedIndependentAmount && pair && routerContract) {
        const dependentCurrency = dependentField === Field.CURRENCY_B ? currencyB : currencyA
        const currencyAmount = await routerContract.quote(pair?.liquidityToken?.address,  dependentField === Field.CURRENCY_B ? tokenA.address : tokenB.address, String(independentAmount.raw))
        const tokenAmount = new TokenAmount(dependentField === Field.CURRENCY_B ? tokenB : tokenA, currencyAmount)
        const dependent = dependentCurrency === MATIC ? CurrencyAmount.ether(tokenAmount.raw) : dependentCurrency === EWT ? CurrencyAmount.ewt(tokenAmount.raw) : tokenAmount
        setDependentAmount(dependent)
        return;
      }
      else {
        setDependentAmount(undefined)
        return;
      }
    } else {
      setDependentAmount(undefined)
      return;
    }
  }

  useEffect(() => {
    getDependentCurrency()
  }, [typedValue, otherTypedValue, dependentField])


  useEffect(() => {
    const parsedAmounts: { [field in Field]: CurrencyAmount | undefined } = {
      [Field.CURRENCY_A]: independentField === Field.CURRENCY_A ? independentAmount : dependentAmount,
      [Field.CURRENCY_B]: independentField === Field.CURRENCY_A ? dependentAmount : independentAmount
    }
    setParsedAmounts(parsedAmounts);
  }, [dependentAmount])

  const price = useMemo(() => {
    if (noLiquidity) {
      const { [Field.CURRENCY_A]: currencyAAmount, [Field.CURRENCY_B]: currencyBAmount } = parsedAmounts
      if (currencyAAmount && currencyBAmount) {
        return new Price(currencyAAmount.currency, currencyBAmount.currency, currencyAAmount.raw, currencyBAmount.raw)
      }
      return undefined
    } else {
      const wrappedCurrencyA = wrappedCurrency(currencyA, chainId)
      return pair && wrappedCurrencyA ? pair.priceOf(wrappedCurrencyA) : undefined
    }
  }, [chainId, currencyA, noLiquidity, pair, parsedAmounts])
  // liquidity minted
  const liquidityMinted = useMemo(() => {
    const { [Field.CURRENCY_A]: currencyAAmount, [Field.CURRENCY_B]: currencyBAmount } = parsedAmounts
    const [tokenAmountA, tokenAmountB] = [
      wrappedCurrencyAmount(currencyAAmount, chainId),
      wrappedCurrencyAmount(currencyBAmount, chainId)
    ]
    if (pair && totalSupply && tokenAmountA && tokenAmountB) {
      let liquidityMinted = undefined
      
      // this try-catch catches "Invariant failed: TOKEN" and "InsufficientInputAmountError"
      // and the frontend does not break
      try{
        liquidityMinted = pair.getLiquidityMinted(totalSupply, tokenAmountA, tokenAmountB)
      }catch (error){
        console.error(error)
      }

      return liquidityMinted
    } else {
      return undefined
    }
  }, [parsedAmounts, chainId, pair, totalSupply])

  const poolTokenPercentage = useMemo(() => {
    if (liquidityMinted && totalSupply) {
      return new Percent(liquidityMinted.raw, totalSupply.add(liquidityMinted).raw)
    } else {
      return undefined
    }
  }, [liquidityMinted, totalSupply])

  let error: string | undefined
  if (!account) {
    error = 'Connect Wallet'
  }

  if (pairState === PairState.INVALID) {
    error = error ?? 'Invalid pair'
  }

  if (!parsedAmounts[Field.CURRENCY_A] || !parsedAmounts[Field.CURRENCY_B]) {
    error = error ?? 'Enter an amount'
  }

  const { [Field.CURRENCY_A]: currencyAAmount, [Field.CURRENCY_B]: currencyBAmount } = parsedAmounts

  if (currencyAAmount && currencyBalances?.[Field.CURRENCY_A]?.lessThan(currencyAAmount)) {
    error = 'Insufficient ' + currencies[Field.CURRENCY_A]?.symbol + ' balance'
  }

  if (currencyBAmount && currencyBalances?.[Field.CURRENCY_B]?.lessThan(currencyBAmount)) {
    error = 'Insufficient ' + currencies[Field.CURRENCY_B]?.symbol + ' balance'
  }

  return {
    dependentField,
    currencies,
    pair,
    pairState,
    currencyBalances,
    parsedAmounts,
    price,
    noLiquidity,
    isEmpty,
    liquidityMinted,
    poolTokenPercentage,
    error
  }
}
